import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import { useContext } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { StripeGenericPayForm } from './StripeGenericPayForm';
import { AdyenPayForm } from './AdyenPayForm';
import { NuveiGeneralPayForm } from './NuveiGeneraPayForm';
import { VenmoPayForm } from './VenmoPayForm';
import { ChargebeeGenericPayForm } from './ChargebeeGenericPayForm';

export const GenericPayForm = ({ paymentMethod }: { paymentMethod: PaymentMethodEnum }) => {
  const { paymentMethodsSettings } = useContext(CheckoutContext);
  const settings = paymentMethodsSettings.find((pm) => pm.paymentMethodName === paymentMethod);
  const provider = settings?.provider;
  if (!provider) {
    throw new Error(`Provider for payment method ${paymentMethod} not found`);
  }
  if (provider === ProviderEnum.STRIPE) {
    // Backend contract: when the Stripe wire identifier differs from our
    // canonical `paymentMethodName` (e.g. `afterpay`→`afterpay_clearpay`,
    // `verifon`→`verifone`, `cash_app`→`cashapp`), the boot payload sets
    // `pspPaymentMethodName` to the Stripe-side string. Otherwise it's null
    // and we forward the canonical name as-is — which is the identity case
    // for the long tail of APMs (kakao_pay, ideal, klarna, …) and means
    // adding a new APM to the enum + backend is a zero-FE-change op.
    const stripePaymentMethod = settings.pspPaymentMethodName ?? paymentMethod;
    return <StripeGenericPayForm paymentMethod={stripePaymentMethod} />;
  }
  if (provider === ProviderEnum.ADYEN) {
    return <AdyenPayForm selectedPaymentMethod={paymentMethod} />;
  }
  if (provider === ProviderEnum.NUVEI) {
    return <NuveiGeneralPayForm />;
  }
  if (provider === ProviderEnum.BRAINTREE && paymentMethod === PaymentMethodEnum.VENMO) {
    return <VenmoPayForm />;
  }
  // Subscriptions: the gateway's routed APMs (Kakao Pay, Naver Pay, KR card, …)
  // through Chargebee's Payment Component. The form resolves the wire id
  // (`pspPaymentMethodName ?? paymentMethodName`) itself, same contract as Stripe.
  if (provider === ProviderEnum.CHARGEBEE) {
    return <ChargebeeGenericPayForm paymentMethod={paymentMethod} />;
  }
  throw new Error(`Unsupported provider for payment method ${paymentMethod}: ${provider}`);
};
